'use strict';

import Helpers from './Helpers';
import ArgValidator from 'bam-arg-validator';
const cloneDeep = Helpers.cloneDeep;

const SUPPORTED_OPTION_SPECS = {
    // If true, active=false teams are allowed in activeTeamsArr (the case for NHL re Vegas in Q1 2017)
    inactivesAllowedInActiveArr: {
        expectedType: 'boolean',
        defaultValue: false
    },
    // If true, remove inactive teams from activeTeamsArr (the case for NHL re Vegas in Q1 2017)
    removeInactivesFromActiveArr: {
        expectedType: 'boolean',
        defaultValue: false
    },
    // A function which, passed a statsapi team, derives and sets its 'slug' property (no-op if data has slugs)
    addSlugToActiveTeamsFn: {
        expectedType: 'function',
        defaultValue: function (team) {
            team.slug = team.franchise.teamName.toLowerCase().trim().replace(/[^a-z]/g, '');
        }
    },
    // Like addSlugToActiveTeamsFn but for inactives
    addSlugToInactiveTeamsFn: {
        expectedType: 'function',
        defaultValue: null
    }
};

let defaultOptions = {};
let optionKeys = Object.keys(SUPPORTED_OPTION_SPECS);
let key;
for (let i = 0, ln = optionKeys.length; i < ln; i++) {
    key = optionKeys[i];
    defaultOptions[key] = SUPPORTED_OPTION_SPECS[key].defaultValue;
}

/**
 * Property-agnostic base class for property-specific team-data-normalizer classes such as MlbTeamData which operate
 * on data from the Sports Data API ("statsapi").
 *
 * _Nothing_ property-specific belongs in this base class.
 */
class BamTeamData {

    /**
     * @param {Array} activeTeamsArr A team array in this format: http://qa-statsapi.mlb.com/api/v1/teams/?sportId=1
     * @param {Array} inactiveTeamsArr Optional depending on consumer: A team array in the format above
     * @param {object} options Options as follows (all are optional; defaults noted below):<ul>
     *  <li><i>inactivesAllowedInActiveArr:</i> If true, teams in <code>activeTeamsArr</code> whose
     *  <code>active</code> property is
     *  false are permitted (as for NHL in Jan 2017, when activeTeams contains an inactive (because it's a "future
     *  team") team, the Vegas team. If false and an inactive team is found, the constructor will throw an error.
     *  Default: false.</li>
     *  <li><i>removeInactivesFromActiveArr:</i> If true (and if the "inactivesAllowedInActiveArr"
     *  option is true), inactive teams (such as the Vegas team in the previous item) will be removed. Default:
     *  false.</li>
     *  <li><i>addSlugToActiveTeamsFn:</i> The function used to derive the team's slug from its existing properties.
     *  Pass null to signal no-op. Default: a function which returns a slug based on the team's franchise
     *  name (which is language-agnostic, the preferred behavior for most properties).</li>
     *  <li><i>addSlugToInactiveTeamsFn:</i> See previous item for description. Default: null (i.e., no-op).</li>
     * </ul>
     * @constructor
     */
    constructor(activeTeamsArr, inactiveTeamsArr, options) {
        Helpers.validateOptions(options, SUPPORTED_OPTION_SPECS); // throw error if options not valid
        this._options = Object.assign(cloneDeep(defaultOptions), options || {}); // now we have default vals for missing options
        Helpers.validateStatsapiTeamsArr(activeTeamsArr, 'activeTeamsArr',
            this._options.inactivesAllowedInActiveArr, true);
        if (inactiveTeamsArr) {
            Helpers.validateStatsapiTeamsArr(inactiveTeamsArr, 'inactiveTeamsArr', false, false);
        }

        if (!this._options.removeInactivesFromActiveArr) {
            this._activeTeams = cloneDeep(activeTeamsArr);
        } else {
            this._activeTeams = activeTeamsArr.filter(function (team) {
                return team.active === true;
            });
        }

        this._inactiveTeams = cloneDeep(inactiveTeamsArr || []);

        // Following two operations will have no effect if the addSlug fn is no-op (or falsy):
        Helpers.extendTeams(this._activeTeams, this._options.addSlugToActiveTeamsFn);
        Helpers.extendTeams(this._inactiveTeams, this._options.addSlugToInactiveTeamsFn);

        this._teamIdsToTeams = {};
        this._activeTeamSlugsToTeams = {};

        this._allTeams = this._activeTeams.concat(this._inactiveTeams);
        let team;
        for (let i = 0, ln = this._allTeams.length; i < ln; i++) {
            team = this._allTeams[i];
            this._teamIdsToTeams[team.id] = team;
            if (team.active) {
                this._activeTeamSlugsToTeams[team.slug] = team;
            }
        }
    }

    /**
     * Returns active teams.
     * @return {Array} An array of active teams.
     */
    getActiveTeams() {
        return this._activeTeams;
    }

    /**
     * Returns inactive teams.
     * @return {Array} An array of inactive teams ([] if there are none; never null/undefined).
     */
    getInactiveTeams() {
        return this._inactiveTeams;
    }

    /**
     * Returns the slug for the team (e.g. "devils"), or null if !team or !team.slug.  Applies to active and inactive
     * teams.
     * @param {string | number} teamId Numeric team ID, as a string or number
     * @return {string}
     */
    getTeamSlug(teamId) {
        let team = this._teamIdsToTeams[parseInt(teamId)];
        if (!team || !team.slug) {
            return null;
        }
        return team.slug;
    }

    /**
     *
     * @param teamId
     * @throws Error if called; subclasses may optionally implement this method
     */
    getTeamColors(teamId) {
        Helpers.throwNotImplementedError('getTeamColors');
    }

    /**
     * Returns the statsapi team with the specified teamId, if it exists. Team may be active or inactive.
     * @param teamId
     * @return {object}
     */
    getTeam(teamId) {
        return this._teamIdsToTeams[teamId];
    }

    /**
     * Returns the team's public-facing abbreviation (e.g. for MLB, LAA, not ANA).
     * @param {string | number} teamId Numeric ID as number or string
     * @return {object} The team, or null if team not found
     */
    getTeamAbbrev(teamId) {
        return this._getTeamProperty(teamId, 'abbreviation');
    }

    /**
     * Return the team whose `key` property is `value`
     * @param {string} key
     * @param {*} value
     * @return {object} The team if found, or null
     */
    getTeamByKeyValue(key, value) {
        return (this._allTeams.find(function (el) {
            return el[key] === value;
        }) || null);
    }

    /**
     * Returns true if the specified team exists
     * @param teamId {string | number} Numeric team ID, as a string or number
     * @return {boolean}
     */
    isTeamExists(teamId) {
        return !(!this._teamIdsToTeams[parseInt(teamId)]);
    }

    /**
     * Returns true if the specified team is active.
     * @param teamId {string | number} Numeric team ID, as a string or number
     * @return {boolean}
     */
    isTeamActive(teamId) {
        if (!this.isTeamExists(teamId)) {
            return false;
        }
        return this._teamIdsToTeams[parseInt(teamId)].active;
    }


    /**
     * Returns the team's specified property, or undefined if the team doesn't exist or the team exists but its property
     * doesn't
     * @param {string | number} teamId Numeric ID as number or string
     * @param {string} property The property
     * @return {object} The team, or null if team not found
     * @private
     */
    _getTeamProperty(teamId, property) {
        let team = this._teamIdsToTeams[teamId];
        return (!team ? undefined : team[property]);
    }

    /**
     * Static method which, passed either a BANE-controller <code>req</code> or a BANE browser <code>window</code>,
     * returns an array of teams
     * in statsapi format (see e.g. http://qa-statsapi.mlb.com/api/v1/teams/?sportId=1).  Returns null if a team array
     * could not be found.
     * @param {object} ctx A BANE controller request or browser window
     * @param clientArrAsDotNotatedStr e.g. "team_info" if arr is window.team_info
     * @param serverArrAsDotNotatedStr e.g. "path.to.teamArr" if arr is req.path.to.teamArr
     * @return {Array} A copy of the array found in ctx, or null
     */

    static getTeamArrayFromReqOrWindow(ctx, clientArrAsDotNotatedStr, serverArrAsDotNotatedStr) {
        return Helpers.getTeamArrayFromReqOrWindow(ctx, clientArrAsDotNotatedStr, serverArrAsDotNotatedStr);
    }

    /**
     * Return the _Helpers class; for use by subclasses
     * @return {_Helpers}
     */
    static getHelpers() {
        return Helpers;
    }
}

export default BamTeamData;

